import React from 'react';
import Konva from 'konva';
import classNames from 'classnames/bind';
import { Circle, Layer, Line, Stage } from 'react-konva';
import { useClickOutside, useToggle } from '@mantine/hooks';

import { Icon } from 'components/Icon/IconDeprecated';

import {
  denormalizeROIShapes,
  normalizeROIShapes,
} from './services/normalize_shapes';
import { getDefaultLabel } from './services/get_default_label';
import {
  getPointerClickPosition,
  getPointerPosition,
} from './services/get_pointer_position';
import { isValidShape } from './services/is_valid_shape';
import { ROIAnchorPoint } from './components/AnchorPoint';
import { ROICanvasInputs } from './components/Inputs';
import { ROIShape } from './components/Shape';

import { ROICanvasType } from './types';
import { ROIToolbar } from './components/Toolbar';
import { ROI_CONFIG } from './config';

import styles from './ROICanvas.module.scss';

const c = classNames.bind(styles);

export type ROICanvasProps = {
  children?: React.ReactNode;
  type?: ROICanvasType;
  defaultValue?: Record<string, number[]>;
  onChange?: (value: Record<string, number[]>) => void;
};

const DBLCLICK_DELTA = 10;

function findClosestSegmentInsertIndex(
  points: number[],
  [px, py]: readonly [number, number]
): number | null {
  // Need at least 2 vertices (1 segment)
  if (points.length < 4) {
    return null;
  }

  let bestDist2 = Number.POSITIVE_INFINITY;
  let bestSegmentIdx: number | null = null;

  for (let i = 0; i <= points.length / 2 - 2; i += 1) {
    const ax = points[i * 2];
    const ay = points[i * 2 + 1];
    const bx = points[i * 2 + 2];
    const by = points[i * 2 + 3];

    const abx = bx - ax;
    const aby = by - ay;
    const apx = px - ax;
    const apy = py - ay;

    const abLen2 = abx * abx + aby * aby;
    if (abLen2 === 0) {
      continue;
    }

    let t = (apx * abx + apy * aby) / abLen2;
    t = Math.max(0, Math.min(1, t));

    const cx = ax + t * abx;
    const cy = ay + t * aby;
    const dx = px - cx;
    const dy = py - cy;
    const dist2 = dx * dx + dy * dy;

    if (dist2 < bestDist2) {
      bestDist2 = dist2;
      bestSegmentIdx = i;
    }
  }

  if (bestSegmentIdx === null) {
    return null;
  }

  // Insert after the segment start vertex.
  return (bestSegmentIdx + 1) * 2;
}

function ROICanvas({
  children,
  defaultValue,
  type = 'polygon',
  onChange,
}: ROICanvasProps) {
  /**
   * Rerender helper to construct shapes with ref but still rerender on update
   */
  const [, rerender] = React.useState<number>(0);

  const stageRef = React.useRef<Konva.Stage>(null);

  const clickPosition = React.useRef<Konva.Vector2d | null>(null);
  const ignoreNextStageClickRef = React.useRef(false);

  const lineRef = React.useRef<Konva.Line>(null);
  const shapeRef = React.useRef<Konva.Line>(null);
  const [shapeStart, setShapeStart] = React.useState<
    readonly [number, number] | null
  >(null);

  const [selectedShape, setSelectedShape] = React.useState<string | null>(null);
  const [selectedAnchor, setSelectedAnchor] =
    React.useState<Konva.Circle | null>(null);

  const [width, setWidth] = React.useState<number>();
  const [height, setHeight] = React.useState<number>();

  const [shapes, setShapes] = React.useState<Record<string, number[]>>({});

  const isInitialized = React.useRef(false);

  const normalizedShapes = React.useMemo(() => {
    const width = stageRef.current?.width();
    const height = stageRef.current?.height();

    if (!width || !height) {
      return null;
    }

    return normalizeROIShapes(shapes, width, height);
  }, [shapes]);

  const [isFullscreen, toggleFullscreen] = useToggle();
  const [isShowingLabels, toggleLabels] = useToggle([true, false]);

  const closeCurrentShape = React.useCallback(() => {
    const shape = shapeRef.current;
    const line = lineRef.current;
    const points = shape?.points();

    if (
      !line ||
      !shape ||
      !points ||
      !isValidShape(points, type) ||
      !stageRef.current
    ) {
      return;
    }

    const width = stageRef.current.width();
    const height = stageRef.current.height();

    setShapes((shapes) => {
      const newShapes = {
        ...shapes,
        [getDefaultLabel(shapes, type)]: points,
      };
      onChange?.(normalizeROIShapes(newShapes, width, height));
      return newShapes;
    });

    line.points([]);
    shape.points([]);
    setShapeStart(null);
  }, [type, onChange]);

  function handleStageDblClick(event: Konva.KonvaEventObject<MouseEvent>) {
    // Konva dblclick also triggers click events; avoid immediately re-processing.
    ignoreNextStageClickRef.current = true;
    event.cancelBubble = true;

    if (!shapeRef.current?.points().length) {
      return;
    }

    closeCurrentShape();
    clickPosition.current = null;
  }

  function handleCancel() {
    const isDrawing =
      Boolean(shapeStart) || Boolean(shapeRef.current?.points().length);

    // Cancel current drawing (same as Esc)
    if (isDrawing) {
      lineRef.current?.points([]);
      shapeRef.current?.points([]);
      clickPosition.current = null;
      setShapeStart(null);
      setSelectedShape(null);
      setSelectedAnchor(null);
      rerender((num) => num + 1);
      return;
    }

    // Deselect selected shape
    if (selectedShape !== null) {
      setSelectedShape(null);
      setSelectedAnchor(null);
    }
  }

  function handleCurrentShapeClick(event: Konva.KonvaEventObject<MouseEvent>) {
    event.cancelBubble = true;

    const stage = stageRef.current;
    const pointerPos = getPointerClickPosition(stage);
    const currentPoints = shapeRef.current?.points() ?? [];

    if (!stage || !pointerPos || !shapeRef.current) {
      return;
    }

    // Support "double-click to complete" even when clicking the current stroke
    // (which doesn't bubble up to the Stage click handler).
    if (clickPosition.current) {
      const { x: ax, y: ay } = clickPosition.current;
      const dx = Math.abs(pointerPos.x - ax);
      const dy = Math.abs(pointerPos.y - ay);

      if (dx < DBLCLICK_DELTA && dy < DBLCLICK_DELTA) {
        closeCurrentShape();
        clickPosition.current = null;
        return;
      }
    }

    const insertIdx = findClosestSegmentInsertIndex(currentPoints, [
      pointerPos.x,
      pointerPos.y,
    ]);

    if (insertIdx === null) {
      return;
    }

    const nextPoints = [
      ...currentPoints.slice(0, insertIdx),
      pointerPos.x,
      pointerPos.y,
      ...currentPoints.slice(insertIdx),
    ];

    shapeRef.current.points(nextPoints);
    clickPosition.current = stage.pointerPos;
    rerender((num) => num + 1);
  }

  function handleCurrentAnchorMove(
    index: number,
    event: Konva.KonvaEventObject<DragEvent>
  ) {
    const stage = stageRef.current;
    const current = shapeRef.current;

    if (!stage || !current) {
      return;
    }

    const anchorPos = getPointerPosition(stage);

    if (anchorPos) {
      event.target.x(anchorPos.x);
      event.target.y(anchorPos.y);
    }

    const points = [...current.points()];
    points[index * 2] = event.target.x();
    points[index * 2 + 1] = event.target.y();
    current.points(points);
    rerender((num) => num + 1);
  }

  function handleCurrentAnchorDblClick(
    event: Konva.KonvaEventObject<MouseEvent>
  ) {
    // After the first click of a double-click, we render an anchor at that
    // position. The second click often hits that anchor (not the Stage), so we
    // need to support completion from here too.
    ignoreNextStageClickRef.current = true;
    event.cancelBubble = true;

    if (!shapeRef.current?.points().length) {
      return;
    }

    closeCurrentShape();
    clickPosition.current = null;
  }

  function handleStageClick() {
    if (ignoreNextStageClickRef.current) {
      ignoreNextStageClickRef.current = false;
      return;
    }

    // Reset selection
    setSelectedShape(null);
    setSelectedAnchor(null);

    const stage = stageRef.current;
    const pointerPos = getPointerClickPosition(stage);

    if (!stage || !pointerPos || !shapeRef.current) {
      return;
    }

    const { x, y } = pointerPos;

    if (!shapeRef.current.points().length) {
      setShapeStart([x, y]);
    }

    const points = [...shapeRef.current.points(), x, y];

    if (!clickPosition.current) {
      clickPosition.current = stage.pointerPos;
      shapeRef.current.points(points);
      rerender((num) => num + 1);
      return;
    }

    const { x: ax, y: ay } = clickPosition.current;

    const dx = Math.abs(x - ax);
    const dy = Math.abs(y - ay);

    // Simulated double-click
    if (dx < DBLCLICK_DELTA && dy < DBLCLICK_DELTA) {
      closeCurrentShape();
      clickPosition.current = null;
      return;
    }

    shapeRef.current.points(points);
    clickPosition.current = stage.pointerPos;
    rerender((num) => num + 1);
  }

  function handleStageMouseMove({
    target,
  }: Konva.KonvaEventObject<MouseEvent>) {
    if (
      !stageRef.current?.pointerPos ||
      !shapeRef.current ||
      !lineRef.current
    ) {
      return;
    }

    const targetType = target.getClassName();
    const currentShape = shapeRef.current.points();

    if (currentShape.length >= 2) {
      const [ax, ay] = currentShape.slice(-2);

      // Snap to existing anchors
      if (targetType === 'Circle') {
        lineRef.current.points([ax, ay, target.x(), target.y()]);
        return;
      }

      const pointerPos = getPointerClickPosition(stageRef.current);

      if (pointerPos) {
        lineRef.current.points([ax, ay, pointerPos.x, pointerPos.y]);
      }
    }
  }

  function handleStartPointClick(event: Konva.KonvaEventObject<MouseEvent>) {
    event.cancelBubble = true;
    closeCurrentShape();
  }

  const handleShapeChange = React.useCallback(
    (label: string, shape: number[]) => {
      if (!stageRef.current) {
        return;
      }

      const width = stageRef.current.width();
      const height = stageRef.current.height();

      setShapes((previousShapes) => {
        const updatedShapes = { ...previousShapes, [label]: shape };
        onChange?.(normalizeROIShapes(updatedShapes, width, height));
        return updatedShapes;
      });
    },
    [onChange]
  );

  const handleShapeRename = React.useCallback(
    (oldLabel: string, newLabel: string) => {
      if (!stageRef.current) {
        return;
      }

      const width = stageRef.current.width();
      const height = stageRef.current.height();

      setShapes((previousShapes) => {
        const { [oldLabel]: shape, ...remainingShapes } = previousShapes;
        const updatedShapes = {
          ...remainingShapes,
          [newLabel]: shape,
        };
        onChange?.(normalizeROIShapes(updatedShapes, width, height));
        return updatedShapes;
      });
    },
    [onChange]
  );

  function deleteSelectedShape() {
    if (!selectedShape || !stageRef.current) {
      return;
    }

    const width = stageRef.current.width();
    const height = stageRef.current.height();

    setShapes(({ [selectedShape]: deletedShape, ...remainingShapes }) => {
      onChange?.(normalizeROIShapes(remainingShapes, width, height));
      return remainingShapes;
    });
    setSelectedShape(null);
  }

  function insertThreePointLine() {
    if (!stageRef.current) {
      return;
    }

    const width = stageRef.current.width();
    const height = stageRef.current.height();

    const threePointLine = denormalizeROIShapes(
      {
        [getDefaultLabel(shapes, type)]: [0, 0.5, 0.5, 0.5, 1, 0.5],
      },
      width,
      height
    );

    setShapes((previousShapes) => {
      const updatedShapes = {
        ...previousShapes,
        ...threePointLine,
      };
      onChange?.(normalizeROIShapes(updatedShapes, width, height));
      return updatedShapes;
    });
  }

  function insertEightPointRectangle() {
    if (!stageRef.current) {
      return;
    }

    const width = stageRef.current.width();
    const height = stageRef.current.height();

    const eightPointRectangle = denormalizeROIShapes(
      {
        [getDefaultLabel(shapes, type)]: [0, 0, 1, 0, 1, 1, 0, 1],
      },
      width,
      height
    );

    setShapes((previousShapes) => {
      const updatedShapes = {
        ...previousShapes,
        ...eightPointRectangle,
      };
      onChange?.(normalizeROIShapes(updatedShapes, width, height));
      return updatedShapes;
    });
  }

  const handleShapeDelete = React.useCallback(
    (label: string) => {
      if (!stageRef.current) {
        return;
      }

      const width = stageRef.current.width();
      const height = stageRef.current.height();

      setShapes(({ [label]: deletedShape, ...remainingShapes }) => {
        onChange?.(normalizeROIShapes(remainingShapes, width, height));
        return remainingShapes;
      });
      setSelectedShape(null);
    },
    [onChange]
  );

  const handleManualChange = React.useCallback(
    (shapes: Record<string, number[]>) => {
      if (!stageRef.current) {
        return;
      }

      const width = stageRef.current.width();
      const height = stageRef.current.height();

      const denormalized = denormalizeROIShapes(shapes, width, height);
      setShapes(denormalized);
      onChange?.(shapes);
    },
    [onChange]
  );

  // Initialize from defaultValue but only if shapes are empty
  React.useEffect(() => {
    if (
      !defaultValue ||
      Object.keys(shapes).length !== 0 ||
      !width ||
      !height
    ) {
      return;
    }

    if (!isInitialized.current) {
      // Set internal state to the denormalized value of defaultValue
      const denormalizedValue = denormalizeROIShapes(
        defaultValue,
        width,
        height
      );
      setShapes(denormalizedValue);

      // FIXME: emitting on initialization doesn't make any sense but seems to be
      // necessary to not lose the initial ROI shapes when editing any other field
      onChange?.(defaultValue);
      isInitialized.current = true;
    }
  }, [defaultValue, width, height, onChange, shapes]);

  React.useEffect(() => {
    if (!stageRef.current) {
      return;
    }

    const container = stageRef.current.container();

    function handleKeyDown(event: KeyboardEvent) {
      switch (event.key) {
        // Finish current shape if valid
        case 'Enter': {
          event.preventDefault();
          closeCurrentShape();
          break;
        }

        // While drawing, remove last segment and reset current line
        case 'Backspace': {
          if (!shapeRef.current?.points().length) {
            break;
          }

          event.preventDefault();

          const updatedShape = shapeRef.current.points().slice(0, -2);
          shapeRef.current.points(updatedShape);

          if (!lineRef.current) {
            break;
          }

          const [ax, ay] = updatedShape.slice(-2);
          const [bx, by] = lineRef.current.points().slice(-2);
          lineRef.current.points([ax, ay, bx, by]);
          break;
        }

        // Stop drawing and deselect shapes
        case 'Escape': {
          event.preventDefault();
          lineRef.current?.points([]);
          shapeRef.current?.points([]);
          setShapeStart(null);
          setSelectedShape(null);
          rerender((num) => num + 1);
          break;
        }
      }
    }

    container.addEventListener('keydown', handleKeyDown);

    return () => {
      container.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeCurrentShape]);

  const wrapRef = useClickOutside(() => {
    if (!shapeRef.current) {
      return;
    }

    lineRef.current?.points([]);
    shapeRef.current?.points([]);
    setShapeStart(null);
    setSelectedShape(null);
    rerender((num) => num + 1);
  });

  // Resize stage and shapes with container element
  React.useEffect(() => {
    const stage = stageRef.current;
    const container = stage?.container();

    if (!stage || !container) {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const width = entry.target.clientWidth;
        const height = entry.target.clientHeight;

        if (
          width <= ROI_CONFIG.padding * 2 ||
          height <= ROI_CONFIG.padding * 2
        ) {
          return;
        }

        const previousWidth = stage.width();
        const previousHeight = stage.height();

        stage.width(width);
        stage.height(height);

        setWidth(width);
        setHeight(height);
        // Not emitting onChange here because no value change occurred.
        setShapes((shapes) => {
          const normalized = normalizeROIShapes(
            shapes,
            previousWidth,
            previousHeight
          );
          return denormalizeROIShapes(normalized, width, height);
        });
      });
    });

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, [onChange]);

  function handleFullscreenClick() {
    if (!wrapRef.current) {
      return;
    }

    toggleFullscreen();

    if (!document.fullscreenElement) {
      wrapRef.current.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }

  React.useEffect(() => {
    function handleFullscreenChange() {
      if (!document.fullscreenElement) {
        toggleFullscreen(false);
      }
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [toggleFullscreen]);

  return (
    <div
      className={c('wrap', { fullscreen: isFullscreen })}
      style={
        {
          '--width': width,
          '--height': height,
        } as React.CSSProperties
      }
      ref={wrapRef}
    >
      <div className={c('canvas')}>
        <div className={c('background')}>{children}</div>

        <Stage
          className={c('stage')}
          onClick={handleStageClick}
          onDblClick={handleStageDblClick}
          onMouseMove={handleStageMouseMove}
          tabIndex={-1}
          ref={stageRef}
        >
          <Layer>
            {Object.entries(shapes).map(([label, shape]) => (
              <ROIShape
                labels={Object.keys(shapes)}
                label={label}
                shape={shape}
                closed={type === 'polygon'}
                isSelected={selectedShape === label}
                selectedAnchor={selectedAnchor}
                isShowingLabels={isShowingLabels}
                onChange={handleShapeChange}
                onRename={handleShapeRename}
                onSelect={setSelectedShape}
                onSelectedAnchorChange={setSelectedAnchor}
                onDelete={handleShapeDelete}
                key={label}
              />
            ))}

            {/* Line from last point to cursor position */}
            <Line {...ROI_CONFIG.line} ref={lineRef} />

            {/* Current shape */}
            <Line
              {...ROI_CONFIG.line}
              ref={shapeRef}
              onClick={handleCurrentShapeClick}
            />

            {/* Anchor points for current shape (while drawing) */}
            {(shapeRef.current?.points() ?? []).length
              ? Array.from(
                  { length: (shapeRef.current?.points().length ?? 0) / 2 },
                  (_, i) => i
                ).map((i) => (
                  <Circle
                    key={`current-anchor-${i}`}
                    {...ROI_CONFIG.anchor}
                    x={shapeRef.current?.points()[i * 2] ?? 0}
                    y={shapeRef.current?.points()[i * 2 + 1] ?? 0}
                    draggable
                    onMouseDown={(e) => (e.cancelBubble = true)}
                    onDragMove={(event) => handleCurrentAnchorMove(i, event)}
                    onDblClick={handleCurrentAnchorDblClick}
                    hitStrokeWidth={10}
                  />
                ))
              : null}

            {/* Start anchor of current shape to easily close */}
            {shapeStart?.length ? (
              <ROIAnchorPoint
                x={shapeStart[0]}
                y={shapeStart[1]}
                onClick={handleStartPointClick}
              />
            ) : null}
          </Layer>
        </Stage>
      </div>

      <ROIToolbar
        type={type}
        isFullscreen={isFullscreen}
        isShowingLabels={isShowingLabels}
        isDrawing={
          Boolean(shapeStart) || Boolean(shapeRef.current?.points().length)
        }
        selectedShape={selectedShape}
        onCancel={handleCancel}
        onDeleteSelectedShape={deleteSelectedShape}
        onInsertEightPointRectangle={insertEightPointRectangle}
        onInsertThreePointLine={insertThreePointLine}
        onToggleFullscreen={handleFullscreenClick}
        onToggleLabels={toggleLabels}
      />

      <details className={c('advanced')}>
        <summary className={c('advanced-header')}>
          <Icon
            className={c('advanced-indicator')}
            name="chevron-right"
            size="small"
          />
          <span>View values</span>
        </summary>
        <div className={c('advanced-content')}>
          <ROICanvasInputs
            shapes={normalizedShapes}
            onChange={handleManualChange}
          />
        </div>
      </details>
    </div>
  );
}

const memoized = React.memo(ROICanvas);

export { memoized as ROICanvas };
